import { useState, useRef } from "react";
import { PaperClipIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { classNames } from "src/helpers/classNames";
import LoadingOpBg from "src/components/LoadingOpBg";
import Loading from "src/components/Loading";
import { UserAvatar } from "src/components/ui/avatar/Avatar";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { Pencil, TrashIcon, Comment } from "src/components/ui-icons/card";
import { DownloadIcon, FileIcon } from "src/components/ui-icons/upload";
import TGButton from "src/components/ui/TGButton";
import EyeIcon from "src/components/ui-icons/reports/EyeIcon";
import axios from "axios";
import { siteUrl, strapiAdminUrl } from "src/api/config";
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { isPWA } from "src/utils/mobile";
import { normalizeJpeg } from "src/pages/DashboardPage/mytasks/utils/utils";
import { downloadForApp } from "src/utils/downloadForApp";

export interface CommentItem {
    id: number;
    textComment: string;
    files: any[];
    author: string;
    date: string;
}

export interface CommentsInlineFormProps {
    label: string;
    variant?: 'inline' | 'inlineAdd';
    disabled: boolean;
    loading: boolean;
    comments: CommentItem[];
    currentUserEmail: string;
    users: any;
    onSave: (data: { TextComment: string; CommentFiles: File[] }) => void;
    onEditComment: (comment: CommentItem) => void;
    onRemoveComment: (comment: CommentItem) => void;
    setImagesDoc: (images: string[]) => void;
    setPreviewLoading: (loading: boolean) => void;
    previewLoading: boolean;
}

export function CommentsInlineForm({
    label,
    variant = 'inline',
    disabled,
    loading,
    comments,
    currentUserEmail,
    users,
    onSave,
    onEditComment,
    onRemoveComment,
    setImagesDoc,
    setPreviewLoading,
    previewLoading,
}: CommentsInlineFormProps) {
    const [text, setText] = useState('');
    const [showMobileFileActions, setShowMobileFileActions] = useState(false);
    const [showFileZone, setShowFileZone] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [isProcessingFiles, setIsProcessingFiles] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const hasContent = text.trim().length > 0 || pendingFiles.length > 0;
    const isAddVariant = variant === 'inlineAdd';

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setText(e.target.value);
        if (!isAddVariant) return;
        e.target.style.height = 'auto';
        e.target.style.height = `${e.target.scrollHeight}px`;
    };

    const handleSave = () => {
        if (!text.trim()) return;
        onSave({ TextComment: text, CommentFiles: pendingFiles });
        setText('');
        setPendingFiles([]);
        setShowFileZone(false);
        if (textareaRef.current) {
            textareaRef.current.style.height = '';
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleSave();
        }
    };

    const handleDrop = async (
        e: React.DragEvent<HTMLDivElement>
    ) => {
        e.preventDefault();

        const files = Array.from(e.dataTransfer.files);

        await addFiles(files);
    };

    const addFiles = async (files: File[]) => {
        if (files.length === 0) return;

        setIsProcessingFiles(true);

        try {
            const normalizedFiles = await Promise.all(
                files.map(file => normalizeJpeg(file))
            );

            setPendingFiles(prev => [...prev, ...normalizedFiles]);
        } catch (error) {
            console.error('Ошибка обработки файлов:', error);
        } finally {
            setIsProcessingFiles(false);
        }
    };

    const handleFileInput = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = Array.from(e.target.files || []);

        // Сбрасываем значение, чтобы можно было повторно выбрать тот же файл
        e.target.value = '';

        if (files.length === 0) return;

        setShowFileZone(true);
        await addFiles(files);
    };


    const handleMobileImage = async (source: CameraSource) => {
        try {
            setShowMobileFileActions(false);
            const photo = await Camera.getPhoto({
                source,
                resultType: CameraResultType.Uri,
                quality: 90,
                allowEditing: false,
            });

            if (!photo.webPath) throw new Error('Не удалось получить изображение');

            const response = await fetch(photo.webPath);
            if (!response.ok) throw new Error('Не удалось прочитать изображение');

            const blob = await response.blob();
            const mimeType = blob.type || 'image/jpeg';
            const extension = mimeType.split('/')[1]?.replace('jpeg', 'jpg') || 'jpg';
            const file = new File([blob], `photo_${Date.now()}.${extension}`, { type: mimeType });

            setShowFileZone(true);
            await addFiles([file]);
        } catch (error) {
            console.error('Ошибка получения изображения:', error);
        }
    };

    const handleChooseFile = () => {
        setShowMobileFileActions(false);
        fileInputRef.current?.click();
    };


    const toggleFileZone = () => {
        if (!isPWA()) {
            // APK: показываем меню выбора источника файла
            setShowMobileFileActions(prev => !prev);
            return;
        }

        if (showFileZone) {
            fileInputRef.current?.click();
        } else {
            setShowFileZone(true);
        }
    };


    const downloadPendingFile = (file: File) => {
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <FlexCol className={isAddVariant ? "gap-[10px]" : "gap-5"}>
            {/* Label + textarea с иконкой скрепки */}
            <FlexCol className="gap-[10px]">
                <label className="text-[#111928] text-base font-normal leading-[22px]">{label}</label>
                <div className="relative w-full">
                    <textarea
                        ref={textareaRef}
                        value={text}
                        onChange={handleTextChange}
                        onKeyDown={handleKeyDown}
                        disabled={disabled}
                        placeholder="Введите ваш комментарий"
                        rows={isAddVariant ? 1 : 3}
                        className={classNames(
                            "w-full px-5 py-3 pr-10 border border-[#DFE4EA] rounded-[6px] resize-none text-base leading-[22px] text-[#111928] focus:outline-none focus:border-[#637381] disabled:bg-[#E5E7EB] disabled:cursor-not-allowed",
                            isAddVariant ? "max-h-[200px] placeholder:text-[#9CA3AF]" : "pb-8 placeholder:text-[#637381]"
                        )}
                    />
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={toggleFileZone}
                        title="Прикрепить файлы"
                        className={classNames(
                            "absolute p-1.5 rounded transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                            isAddVariant ? "top-1.5 right-1" : "bottom-2 right-2",
                            showFileZone ? "text-[#012169]" : "text-[#637381] hover:bg-gray-100"
                        )}
                    >
                        <PaperClipIcon className="w-6 h-6" />
                    </button>
                </div>

                {!isPWA() && showMobileFileActions && (
                    <div className="mt-2 flex flex-col gap-2 rounded-lg border border-F[#DFE4EA] p-3">
                        <button type="button" disabled={disabled} onClick={() => handleMobileImage(CameraSource.Camera)} className="flex items-center gap-3 rounded-md p-3 text-left hover:bg-gray-100 disabled:opacity-50">
                            Сделать фото
                        </button>
                        <button type="button" disabled={disabled} onClick={() => handleMobileImage(CameraSource.Photos)} className="flex items-center gap-3 rounded-md p-3 text-left hover:bg-gray-100 disabled:opacity-50">
                            Выбрать из галереи
                        </button>
                        <button type="button" disabled={disabled} onClick={handleChooseFile} className="flex items-center gap-3 rounded-md p-3 text-left hover:bg-gray-100 disabled:opacity-50">
                            Выбрать файл
                        </button>
                    </div>
                )}
            </FlexCol>

            {/* Список прикреплённых файлов (ещё не сохранённых) */}
            {pendingFiles.length > 0 && (
                <FlexCol className="gap-2">
                    {pendingFiles.map((file, i) => (
                        <div key={i} className="bg-[#F3F4F6] flex gap-3 items-center px-4 py-2 rounded-lg">
                            <span className="text-[#637381] flex-shrink-0">
                                <FileIcon />
                            </span>
                            <span className="text-[#1F2A37] text-base flex-1 truncate">
                                {file.name}
                            </span>
                            <div className="flex gap-[10px] items-center flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => downloadPendingFile(file)}
                                    title="Скачать"
                                    className="hover:opacity-70 transition-opacity"
                                >
                                    <DownloadIcon />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}
                                    title="Удалить"
                                    className="[&>svg]:!text-[#1F2A37] hover:opacity-70 transition-opacity"
                                >
                                    <TrashIcon />
                                </button>
                            </div>
                        </div>
                    ))}
                </FlexCol>
            )}

            {/* Зона drag & drop */}
            {showFileZone && isPWA() && !isProcessingFiles && (
                <div
                    className="bg-white border border-dashed border-[#CED4DA] flex flex-col gap-[15px] min-h-[200px] items-center justify-center rounded-lg cursor-pointer hover:border-[#637381] transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                >
                    <span className="text-[#637381]">
                        <FileIcon />
                    </span>
                    <p className="font-medium text-[#637381] text-lg text-center">
                        <span className="text-[#012169]">выберите файл</span>
                    </p>
                    <p className="text-[#637381] text-sm">Размер файлов не более 50MB</p>
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileInput}
            />

            {/* Кнопка сохранения */}
            {isAddVariant
                ? <button
                    type="button"
                    disabled={disabled || !hasContent}
                    onClick={handleSave}
                    className={classNames(
                        "btn-white flex gap-2.5 justify-center items-center w-full h-12 rounded-md shadow-custom text-base font-medium leading-[22px]",
                        disabled || !hasContent ? "cursor-not-allowed opacity-60" : ""
                    )}
                >
                    <span>Добавить комментарий</span>
                    <PlusCircleIcon className="w-5 h-5" />
                </button>
                : hasContent && !disabled && (
                    <div className="flex justify-end">
                        <TGButton type="button" disabled={isProcessingFiles} color="bluedark" onClick={handleSave}>
                            {isProcessingFiles ? 'Обработка...' : 'Сохранить'}
                        </TGButton>
                    </div>
                )
            }

            {/* Список существующих комментариев */}
            {comments.length > 0 && (
                <FlexCol className="relative gap-5 mt-2">
                    {loading && <LoadingOpBg customWrapperClass="z-20" />}
                    {comments.map((comment) => (
                        <div key={comment.id} className="flex p-5 flex-col justify-center items-start gap-5 flex-[1_0%_0%] w-full rounded-lg border border-solid border-[#DFE4EA]">
                            <FlexRow className="justify-between w-full">
                                <div className="flex gap-2 items-center">
                                    <UserAvatar
                                        avatar={users[comment.author] && users[comment.author].avatar}
                                        shortName={
                                            users[comment.author] &&
                                            users[comment.author].displayName &&
                                            users[comment.author].displayName.split(' ')[0][0] +
                                            users[comment.author].displayName.split(' ')[1][0]
                                        }
                                        displayName={users[comment.author] && users[comment.author].displayName}
                                        email={comment.author}
                                    />
                                    <div className="flex flex-col justify-center items-start">
                                        <span className="text-sm text-[#111928]">
                                            {users[comment.author] && users[comment.author].displayName}
                                        </span>
                                        <span className="text-sm text-[#637381]">{comment.date}</span>
                                    </div>
                                </div>
                                {currentUserEmail === comment.author && !disabled && (
                                    <FlexRow className="gap-2 hover:cursor-pointer">
                                        <div onClick={() => onEditComment(comment)}>
                                            <Pencil hover={true} />
                                        </div>
                                        <div onClick={() => onRemoveComment(comment)} className="[&>svg]:!text-[#1F2A37]">
                                            <TrashIcon />
                                        </div>
                                    </FlexRow>
                                )}
                            </FlexRow>
                            <div className="flex gap-2">
                                <Comment />
                                <span
                                    className="text-sm text-[#111928] whitespace-pre-wrap"
                                    dangerouslySetInnerHTML={{
                                        __html: comment.textComment
                                            ? comment.textComment.replace(
                                                /(^|\W)(#[а-я\w][а-я\w-]*)/gi,
                                                '<span style="color: rgb(0,98,155)">$&</span>'
                                            )
                                            : ''
                                    }}
                                />
                            </div>
                            {comment.files.length > 0 && comment.files.map((file: any) => (
                                <div key={file.ID} className="bg-[#F3F4F6] flex gap-3 items-center px-4 py-2 rounded-lg w-full">
                                    <span className="text-[#637381] flex-shrink-0">
                                        <FileIcon />
                                    </span>
                                    <span className="text-[#1F2A37] text-base flex-1 truncate">
                                        {file.FileLeafRef}
                                    </span>
                                    <div className="flex gap-[10px] items-center flex-shrink-0">
                                        {/* Просмотр изображений */}
                                        {(
                                            file.FileLeafRef.indexOf('.jpg') !== -1 ||
                                            file.FileLeafRef.indexOf('.jpeg') !== -1 ||
                                            file.FileLeafRef.indexOf('.gif') !== -1 ||
                                            file.FileLeafRef.indexOf('.png') !== -1
                                        ) && (
                                            <button
                                                type="button"
                                                onClick={() => setImagesDoc([file.FileRef])}
                                                className="text-[#1F2A37] hover:opacity-70 transition-opacity"
                                                title="Просмотр"
                                            >
                                                <EyeIcon />
                                            </button>
                                        )}
                                        {/* Просмотр docx/xlsx */}
                                        {(
                                            file.FileLeafRef.indexOf('.docx') !== -1 ||
                                            file.FileLeafRef.indexOf('.xlsx') !== -1
                                        ) && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setPreviewLoading(true);
                                                    axios.post(
                                                        `${siteUrl}/api/md-documents-files/preview_path`,
                                                        {
                                                            id: file.ID,
                                                            path: file.FileRef.replace(siteUrl, strapiAdminUrl),
                                                        },
                                                        { withCredentials: true }
                                                    ).then(results => {
                                                        setImagesDoc(results.data.map((url: string) => `${siteUrl}${url}`));
                                                        setPreviewLoading(false);
                                                    });
                                                }}
                                                className="text-[#1F2A37] hover:opacity-70 transition-opacity"
                                                title="Просмотр"
                                            >
                                                {previewLoading
                                                    ? <FlexCol className="flex w-6 h-6"><Loading /></FlexCol>
                                                    : <EyeIcon />
                                                }
                                            </button>
                                        )}
                                        {/* Скачать */}
                                        <a
                                            href={file.FileRef}
                                            download
                                            className="text-[#1F2A37] hover:opacity-70 transition-opacity"
                                            title="Скачать"
                                            onClick={async (e) => {
                                                e.stopPropagation();

                                                if (isPWA()) return;

                                                e.preventDefault();
                                                await downloadForApp(file);
                                            }}
                                        >
                                            <DownloadIcon />
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </FlexCol>
            )}
        </FlexCol>
    );
}
